/**
 * The Today screen's data, assembled locally.
 *
 * The screen NEVER renders straight from a network response. It renders from the
 * cached regimen plus cached events plus whatever is still in the outbox, using the
 * same buildDayView the server uses. That is what makes a tap show a checkmark
 * instantly with no signal, and what guarantees the offline screen and the online
 * screen can never disagree.
 */
import {
  buildDayView,
  todayFor,
  type DayView,
  type LogEventInput,
  type StoredEvent,
} from '@rx/shared';
import type { AppConfig } from '../config.js';
import { OfflineError, apiFetch } from './client.js';
import {
  getDayEvents,
  getRegimen,
  outboxEntries,
  putEvents,
  putRegimen,
  type CachedRegimen,
} from '../offline/db.js';

interface RegimenResponse {
  patientId: string;
  tz: string;
  dayStartHour: number;
  isTest?: boolean;
  pharmacy?: { name?: string; phone?: string; address?: string };
  medications: CachedRegimen['medications'];
}

/** Refresh the cached regimen. Safe to fail: the cache is what the screen reads. */
export async function syncRegimen(config: AppConfig, patientId: string): Promise<void> {
  const response = await apiFetch<RegimenResponse>(
    config,
    `/v1/patients/${encodeURIComponent(patientId)}/medications`,
  );
  const regimen: CachedRegimen = {
    patientId,
    tz: response.tz,
    dayStartHour: response.dayStartHour,
    medications: response.medications,
    isTest: response.isTest ?? false,
    fetchedAt: new Date().toISOString(),
  };
  if (response.pharmacy) regimen.pharmacy = response.pharmacy;
  await putRegimen(regimen);
}

/** Refresh a day's confirmed events into the cache. */
export async function syncDay(
  config: AppConfig,
  patientId: string,
  logicalDate: string,
): Promise<void> {
  const view = await apiFetch<DayView & { rawEvents?: unknown }>(
    config,
    `/v1/patients/${encodeURIComponent(patientId)}/days/${logicalDate}`,
  );
  await putEvents(
    patientId,
    view.logicalDate,
    view.events as unknown as Record<string, unknown>[],
  );
}

/** The cached regimen, for checks that must work with no signal. */
export async function localRegimen(patientId: string): Promise<CachedRegimen | undefined> {
  return getRegimen(patientId);
}

export interface LocalDay {
  view: DayView | undefined;
  /** Doses logged but not yet accepted by the server. */
  pendingCount: number;
  /** True when the regimen has never been fetched — the screen cannot be drawn. */
  needsFirstSync: boolean;
}

/**
 * Build today's screen from local state alone.
 *
 * Outbox entries are folded in as though they were already stored, so a dose logged
 * in the basement shows its checkmark immediately and keeps it after a reload.
 */
export async function localDay(patientId: string, now = new Date()): Promise<LocalDay> {
  const regimen = await getRegimen(patientId);
  if (!regimen) return { view: undefined, pendingCount: 0, needsFirstSync: true };

  const patient = {
    patientId,
    displayName: '',
    tz: regimen.tz,
    dayStartHour: regimen.dayStartHour,
    pharmacy: regimen.pharmacy,
    isTest: regimen.isTest ?? false,
  };
  const logicalDate = todayFor(patient, now);

  const [confirmed, queued] = await Promise.all([
    getDayEvents(patientId, logicalDate),
    outboxEntries(),
  ]);

  const pending = queued
    .filter((entry) => entry.patientId === patientId)
    .map((entry) => pendingToStored(entry.event, regimen.tz, logicalDate));

  // A pending entry and its confirmed twin share an eventId; keep the confirmed one.
  const confirmedIds = new Set(confirmed.map((e) => String((e as { eventId?: string }).eventId)));
  const events = [
    ...(confirmed as unknown as StoredEvent[]),
    ...pending.filter((p) => !confirmedIds.has(p.eventId)),
  ];

  return {
    view: buildDayView({ patient, medications: regimen.medications, events, logicalDate, now }),
    pendingCount: pending.length,
    needsFirstSync: false,
  };
}

/**
 * Treat a queued event as if it were stored.
 *
 * The optimistic logicalDate is the current row; the server may file it differently
 * (a late bedtime dose belongs to the previous row) and its answer wins on sync.
 */
function pendingToStored(event: LogEventInput, tz: string, logicalDate: string): StoredEvent {
  const takenAtUtc = new Date(event.clientCapturedAt).toISOString();
  const stored: StoredEvent = {
    eventId: event.eventId,
    eventType: event.eventType,
    takenAtUtc,
    logicalDate,
    timeQuality: 'GOOD',
    actorRole: 'SELF',
    localTime: new Intl.DateTimeFormat('en-GB', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(takenAtUtc)),
  };
  if (event.eventType === 'DOSE_TAKEN') {
    stored.medId = event.medId;
    stored.medVersion = event.medVersion;
    stored.slotId = event.slotId ?? null;
    stored.quantity = event.quantity;
  }
  if (event.eventType === 'SYMPTOM') stored.symptoms = event.symptoms;
  if (event.eventType === 'EXTRA_SUBSTANCE') stored.substanceKey = event.substanceKey;
  return stored;
}

/**
 * Push the outbox, then pull the day back down.
 *
 * Both halves matter. Draining deletes the local outbox entry once the server has it,
 * so without the pull the just-logged event exists in neither place and the checkmark
 * silently reverts to "I took it" a second after it appeared — which looks exactly
 * like the tap failed.
 */
export async function syncAfterWrite(
  config: AppConfig,
  patientId: string,
  logicalDate: string,
): Promise<void> {
  const { drainOutbox } = await import('../offline/outbox.js');
  const outcome = await drainOutbox(config);
  if (outcome.applied > 0) {
    try {
      await syncDay(config, patientId, logicalDate);
    } catch {
      // Offline again already. The event is safely on the server; the local view
      // catches up on the next successful refresh.
    }
  }
}

/** Best-effort refresh; the UI stays usable when it fails. */
export async function refreshAll(
  config: AppConfig,
  patientId: string,
  logicalDate: string,
): Promise<boolean> {
  try {
    await syncRegimen(config, patientId);
    await syncDay(config, patientId, logicalDate);
    return true;
  } catch (error) {
    if (error instanceof OfflineError) return false;
    return false;
  }
}
